import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap";
import api from "../../config/api";
import { useAppDialog } from "./AppDialogProvider";

const MODE_CARDS = [
  {
    key: "add",
    title: "Add Gage",
    description: "Create a new gage entry in the system.",
    icon: "ri-add-circle-line",
    tone: "primary",
  },
  {
    key: "modify",
    title: "Modify Gage",
    description: "Rename an existing gage while keeping the workflow simple.",
    icon: "ri-edit-2-line",
    tone: "warning",
  },
  {
    key: "delete",
    title: "Delete Gage",
    description: "Remove a gage entry after confirmation.",
    icon: "ri-delete-bin-6-line",
    tone: "danger",
  },
];

export default function Gages({ onBack }) {
  const dialog = useAppDialog();

  const [mode, setMode] = useState("");
  const [activeMode, setActiveMode] = useState("");

  const [gageList, setGageList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [hasLoadedGageList, setHasLoadedGageList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");

  const [addGageName, setAddGageName] = useState("");
  const [selectedGage, setSelectedGage] = useState("");
  const [newGageName, setNewGageName] = useState("");

  const resetFormState = useCallback(() => {
    setMode("");
    setActiveMode("");
    setAddGageName("");
    setSelectedGage("");
    setNewGageName("");
    setPageError("");
  }, []);

  const loadGageList = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && hasLoadedGageList) {
        return;
      }

      try {
        setLoadingList(true);
        setPageError("");
        const data = await getGageList();
        setGageList(data || []);
        setHasLoadedGageList(true);
      } catch (error) {
        console.error("Failed to load gage list:", error);
        setPageError("Failed to load gage list.");
        await dialog.error("Failed to load gage list.");
      } finally {
        setLoadingList(false);
      }
    },
    [hasLoadedGageList, dialog]
  );

  useEffect(() => {
    if (activeMode === "modify" || activeMode === "delete") {
      loadGageList(false);
    }
  }, [activeMode, loadGageList]);

  async function handleContinue() {
    if (!mode) {
      await dialog.warning("Please make a selection to continue.");
      return;
    }

    setActiveMode(mode);
    setSelectedGage("");
    setNewGageName("");
    setPageError("");
  }

  async function handleAddGage() {
    if (!addGageName.trim()) {
      await dialog.warning("Please enter a gage name.");
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await addGage({ gage: addGageName.trim() });

      if (success === true) {
        await dialog.success("Gage Successfully Added");
        await loadGageList(true);
        resetFormState();
      } else {
        await dialog.error("Error in adding Gage");
      }
    } catch (error) {
      console.error("Add gage failed:", error);
      setPageError("Error in adding Gage");
      await dialog.error("Error in adding Gage");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleModifyGage() {
    if (selectedGage.trim() === "") {
      await dialog.warning("Please select a gage to modify");
      return;
    }

    if (newGageName.trim() === "") {
      await dialog.warning("Please enter the new name of the gage");
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await modifyGage({
        oldGage: selectedGage.trim(),
        newGage: newGageName.trim(),
      });

      if (success === false) {
        await dialog.error("Could not modify gage.");
      } else {
        await dialog.success("Gage successfully modified.");
        await loadGageList(true);
        resetFormState();
      }
    } catch (error) {
      console.error("Modify gage failed:", error);
      setPageError("Could not modify gage.");
      await dialog.error("Could not modify gage.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteGage() {
    if (selectedGage.trim() === "") {
      await dialog.warning("Please select a gage to delete");
      return;
    }

    const confirmed = await dialog.confirm({
      title: "Delete Gage",
      message: `Are you sure you wish to delete ${selectedGage.trim()}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "error",
    });

    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await deleteGage({ gage: selectedGage.trim() });

      if (success === false) {
        await dialog.error("Could not delete gage.");
      } else {
        await dialog.success("Gage successfully deleted.");
        await loadGageList(true);
        resetFormState();
      }
    } catch (error) {
      console.error("Delete gage failed:", error);
      setPageError("Could not delete gage.");
      await dialog.error("Could not delete gage.");
    } finally {
      setSubmitting(false);
    }
  }

  const activeModeMeta = useMemo(
    () => MODE_CARDS.find((item) => item.key === activeMode) || null,
    [activeMode]
  );

  return (
    <div className="gages-page">
      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="gages-toolbar-main">
                <div className="gages-toolbar-main-left">
                  <Button color="soft-dark" className="gages-action-btn" onClick={onBack}>
                    <i className="ri-arrow-left-line gages-btn-icon me-1" />
                    Back
                  </Button>
                </div>
              </div>

              <div className="gages-toolbar-utility">
                <Button
                  color="primary"
                  className="gages-action-btn gages-utility-btn"
                  onClick={handleContinue}
                  disabled={submitting}
                >
                  <i className="ri-arrow-right-line gages-btn-icon me-1" />
                  Continue
                </Button>

                {activeMode && (
                  <Button
                    color="light"
                    className="gages-action-btn gages-utility-btn border"
                    onClick={resetFormState}
                    disabled={submitting}
                  >
                    <i className="ri-refresh-line gages-btn-icon me-1" />
                    Reset
                  </Button>
                )}

                {(activeMode === "modify" || activeMode === "delete") && (
                  <Button
                    color="secondary"
                    className="gages-action-btn gages-utility-btn"
                    onClick={() => loadGageList(true)}
                    disabled={loadingList || submitting}
                  >
                    <i className="ri-refresh-line gages-btn-icon me-1" />
                    {loadingList ? "Refreshing..." : "Refresh List"}
                  </Button>
                )}

                <Badge color="light" className="gages-info-badge">
                  Queue Size: {gageList.length}
                </Badge>

                <div className="gages-info-pill">
                  <span className="gages-info-label">Selected Action</span>
                  <span className="gages-info-value">
                    {mode ? toTitle(mode) : "None"}
                  </span>
                </div>

                <div className="gages-info-pill">
                  <span className="gages-info-label">Active Workflow</span>
                  <span className="gages-info-value">
                    {activeMode ? toTitle(activeMode) : "Waiting"}
                  </span>
                </div>

                <div className="gages-info-pill">
                  <span className="gages-info-label">List State</span>
                  <span className="gages-info-value">
                    {loadingList ? "Loading" : hasLoadedGageList ? "Cached" : "Not Loaded"}
                  </span>
                </div>

                <div className="gages-refresh-note">
                  {activeModeMeta ? activeModeMeta.title : "Choose an action to continue."}
                </div>
              </div>

              {pageError ? (
                <div className="gages-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <div className="gages-mode-grid mb-4">
                {MODE_CARDS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`gages-mode-card gages-mode-card-${item.tone} ${mode === item.key ? "active" : ""}`}
                    onClick={() => setMode(item.key)}
                  >
                    <div className="gages-mode-icon">
                      <i className={item.icon} />
                    </div>
                    <div className="gages-mode-content">
                      <div className="gages-mode-title">{item.title}</div>
                      <div className="gages-mode-copy">{item.description}</div>
                    </div>
                  </button>
                ))}
              </div>

              {activeMode === "add" && (
                <div className="gages-panel-card">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div>
                      <h5 className="mb-1">Add Gage</h5>
                      <p className="text-muted mb-0">Create a new gage entry in the system.</p>
                    </div>
                    <Badge color="light" className="gages-soft-badge text-uppercase">Create</Badge>
                  </div>

                  <Row className="g-3 align-items-end">
                    <Col xl={8}>
                      <Label className="form-label">Gage Name</Label>
                      <Input
                        type="text"
                        value={addGageName}
                        onChange={(e) => setAddGageName(e.target.value)}
                        placeholder="Enter a new gage name"
                      />
                    </Col>
                    <Col xl={4}>
                      <Button
                        color="success"
                        className="w-100 gages-action-btn"
                        onClick={handleAddGage}
                        disabled={submitting}
                      >
                        <i className="ri-add-circle-line gages-btn-icon me-1" />
                        {submitting ? "Adding..." : "Add Gage"}
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}

              {activeMode === "modify" && (
                <div className="gages-panel-card">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div>
                      <h5 className="mb-1">Modify Gage</h5>
                      <p className="text-muted mb-0">Select an existing gage and update its name.</p>
                    </div>
                    <Badge color="light" className="gages-soft-badge text-uppercase">Update</Badge>
                  </div>

                  {loadingList ? (
                    <div className="text-muted">Loading gage list...</div>
                  ) : (
                    <Row className="g-3 align-items-end">
                      <Col xl={5}>
                        <Label className="form-label">Select Gage</Label>
                        <Input
                          type="select"
                          value={selectedGage}
                          onChange={(e) => setSelectedGage(e.target.value)}
                        >
                          <option value="">-- Select Gage --</option>
                          {gageList.map((gage) => (
                            <option key={gage} value={gage}>
                              {gage}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col xl={5}>
                        <Label className="form-label">New Gage Name</Label>
                        <Input
                          type="text"
                          value={newGageName}
                          onChange={(e) => setNewGageName(e.target.value)}
                          placeholder="Enter updated gage name"
                        />
                      </Col>
                      <Col xl={2}>
                        <Button
                          color="warning"
                          className="w-100 gages-action-btn text-white"
                          onClick={handleModifyGage}
                          disabled={submitting}
                        >
                          <i className="ri-edit-2-line gages-btn-icon me-1" />
                          {submitting ? "Updating..." : "Modify"}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {activeMode === "delete" && (
                <div className="gages-panel-card">
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                    <div>
                      <h5 className="mb-1">Delete Gage</h5>
                      <p className="text-muted mb-0">Select a gage and remove it after confirmation.</p>
                    </div>
                    <Badge color="light" className="gages-soft-badge text-uppercase">Delete</Badge>
                  </div>

                  {loadingList ? (
                    <div className="text-muted">Loading gage list...</div>
                  ) : (
                    <Row className="g-3 align-items-end">
                      <Col xl={9}>
                        <Label className="form-label">Select Gage</Label>
                        <Input
                          type="select"
                          value={selectedGage}
                          onChange={(e) => setSelectedGage(e.target.value)}
                        >
                          <option value="">-- Select Gage --</option>
                          {gageList.map((gage) => (
                            <option key={gage} value={gage}>
                              {gage}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col xl={3}>
                        <Button
                          color="danger"
                          className="w-100 gages-action-btn"
                          onClick={handleDeleteGage}
                          disabled={submitting}
                        >
                          <i className="ri-delete-bin-6-line gages-btn-icon me-1" />
                          {submitting ? "Deleting..." : "Delete"}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .gages-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .gages-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .gages-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .gages-soft-badge {
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 0.55rem 0.85rem;
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          border-radius: 999px;
        }

        .gages-panel-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
          height: 100%;
        }

        .gages-action-btn {
          border-radius: 0.65rem;
          font-weight: 600;
          padding: 0.42rem 0.8rem;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .gages-utility-btn {
          min-width: 110px;
        }

        .gages-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: rgba(15, 23, 42, 0.14);
          color: #0f172a;
          border-color: rgba(15, 23, 42, 0.12);
        }

        .gages-info-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #0f172a;
          background: #f8fafc;
        }

        .gages-info-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 38px;
          padding: 0.45rem 0.75rem;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 0.65rem;
          background: #f8fafc;
          max-width: 320px;
        }

        .gages-info-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          white-space: nowrap;
        }

        .gages-info-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .gages-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #64748b;
          font-weight: 500;
        }

        .gages-error-banner {
          display: flex;
          align-items: center;
          padding: 0.8rem 0.95rem;
          border-radius: 0.8rem;
          border: 1px solid rgba(220, 53, 69, 0.18);
          background: rgba(220, 53, 69, 0.06);
          color: #842029;
          font-weight: 500;
        }

        .gages-mode-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
        }

        .gages-mode-card {
          width: 100%;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          background: #fff;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
          text-align: left;
          transition: all 0.18s ease;
        }

        .gages-mode-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
        }

        .gages-mode-card.active {
          background: rgba(13, 110, 253, 0.08);
          border-color: rgba(13, 110, 253, 0.28);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .gages-mode-icon {
          width: 42px;
          height: 42px;
          border-radius: 0.85rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          flex-shrink: 0;
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
        }

        .gages-mode-card-primary .gages-mode-icon {
          background: rgba(13, 110, 253, 0.12);
          color: #0d6efd;
        }

        .gages-mode-card-warning .gages-mode-icon {
          background: rgba(255, 193, 7, 0.18);
          color: #997404;
        }

        .gages-mode-card-danger .gages-mode-icon {
          background: rgba(220, 53, 69, 0.12);
          color: #dc3545;
        }

        .gages-mode-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 0.2rem;
        }

        .gages-mode-copy {
          color: #64748b;
          font-size: 0.92rem;
          line-height: 1.45;
        }

        @media (max-width: 991.98px) {
          .gages-mode-grid {
            grid-template-columns: 1fr;
          }

          .gages-info-pill {
            max-width: 100%;
          }

          .gages-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function toTitle(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

async function getGageList() {
  const response = await api.get("mafia/gagelist");
  return response.data;
}

async function addGage(request) {
  const response = await api.post("mafia/add-gage", {
    gage: request.gage,
  });

  return response.data;
}

async function modifyGage(request) {
  const response = await api.post("mafia/modify-gage", {
    oldGage: request.oldGage,
    newGage: request.newGage,
  });

  return response.data;
}

async function deleteGage(request) {
  const response = await api.post("mafia/delete-gage", {
    gage: request.gage,
  });

  return response.data;
}